import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
// (Omni の想定尺確認モーダルは廃止済み。想定尺 > モデル最大尺はストーリーボードの
//  動画カード自動分割 (useAutoVideoCardSegmentation) で正式サポートされ、setup 側は
//  ScenarioSettingsSection のインライン注記で案内する)
import { useGenerationState, type VideoGenerationMode, type CastingActor } from '../../state/useGenerationState';
import type { VideoSettings } from '../VideoGenerationSettings';
import type { Mode } from '../types';
import { isCompatibleFrameAspectRatio, splitCastForSeedanceSwitch } from '../utils';

export interface ActorClearConfirmState {
  fromModel: string;
  toModel: string;
  side: 'castingToSeedance' | 'actorToVeo';
  /** side='castingToSeedance' で人間俳優のみ解除・キャラクター俳優は維持する場合に、
   * 維持するキャラクター俳優 (順序維持。先頭が次の主俳優)。人間俳優のみ選択中で全解除
   * する場合は空配列 (計画書§4 P5。レビュー指摘 P2)。 */
  keepCastActors?: CastingActor[];
}

export interface UseScenarioConfirmDialogsParams {
  mode: Mode;
  /** 現在の動画生成モード (prompt / scenario でストアのキーが異なるため親から受け取る)。 */
  videoGenerationMode: VideoGenerationMode;
  /** 現在のモードに対応する永続セッター。 */
  setVideoGenerationMode: (next: VideoGenerationMode) => void;
  /** プロンプト入力モード側のセッター (参考画像モードへの切替提案で使う)。 */
  setPromptVideoGenerationMode: (next: VideoGenerationMode) => void;
  castingGenderFilter: string;
  castingAgeFilter: number | null;
}

export interface ScenarioConfirmDialogs {
  /** 動画生成モードの切替 (非対応素材があるときは確認モーダルを挟む)。 */
  handleVideoGenerationModeChange: (next: VideoGenerationMode) => void;

  actorClearConfirm: ActorClearConfirmState | null;
  onActorClearConfirm: () => void;
  onActorClearCancel: () => void;

  showSeedanceFaceWarning: boolean;
  onSeedanceFaceWarningConfirm: () => void;
  onSeedanceFaceWarningCancel: () => void;

  showVeoFaceOffNotice: boolean;
  onVeoFaceOffNoticeConfirm: () => void;
  onVeoFaceOffNoticeCancel: () => void;

  showKeepReferenceConfirm: boolean;
  /** 「維持」する側の表示名 (確認モーダルの文言用)。切替を押した時点のモードで決まる
   *  (AIおまかせ / モーショングラフィックス)。 */
  keepReferenceModeLabel: string;
  onKeepReferenceConfirm: () => void;
  onKeepReferenceCancel: () => void;

  showSwitchToReferenceConfirm: boolean;
  onSwitchToReferenceConfirm: () => void;
  onSwitchToReferenceCancel: () => void;

  /** 全フレーム削除の確認 (JSX は WorkPanel 側にある)。 */
  showClearAllFramesConfirm: boolean;
  setShowClearAllFramesConfirm: Dispatch<SetStateAction<boolean>>;
}

/**
 * ScenarioGenerator の確認ダイアログ群 (state + 発火 effect + ハンドラ) を集約したフック。
 * JSX は ScenarioDialogs が描画する (全フレーム削除のみ WorkPanel 内)。
 */
export function useScenarioConfirmDialogs({
  mode,
  videoGenerationMode,
  setVideoGenerationMode,
  setPromptVideoGenerationMode,
  castingGenderFilter,
  castingAgeFilter,
}: UseScenarioConfirmDialogsParams): ScenarioConfirmDialogs {
  const {
    selectedAssets,
    videoSettings,
    setVideoSettings,
    avoidFaces,
    setAvoidFaces,
    illustrationStoryboard,
    setIllustrationStoryboard,
    selectedCasting,
    setSelectedCasting,
    additionalCastings,
    setAdditionalCastings,
    selectedSeedancePresetActor,
    setSelectedSeedancePresetActor,
    setAdditionalSeedancePresetActors,
  } = useGenerationState();

  // プロンプト+フレーム動画生成モード時、フレーム非対応アスペクト比の素材が追加されたら
  // 参考画像モードへの切替を提案する
  const [showSwitchToReferenceConfirm, setShowSwitchToReferenceConfirm] = useState(false);
  // 参考画像モードから「フレームから動画生成」への切替時、非対応素材があれば警告
  const [showKeepReferenceConfirm, setShowKeepReferenceConfirm] = useState(false);
  // 確認モーダルの「維持」で戻す先。切替を押した時点のモード (reference / motion) に固定する
  // (motion から来たときに AIおまかせ へ戻してしまわないため・motion-graphics-mode-plan.md §5.1)。
  const [keepReferenceFromMode, setKeepReferenceFromMode] = useState<'reference' | 'motion'>('reference');

  const handleVideoGenerationModeChange = useCallback((next: VideoGenerationMode) => {
    // Lite モデルは参考画像モード自体が選べず frame へ自動切替されるため、
    // 「参考画像モードのまま」を勧める確認モーダルは出さない（戻れる先が無い）
    // (モーショングラフィックスは Lite でも選べるので、motion から来たときは Lite でも確認する)
    const isLiteModel = videoSettings.videoModel === 'veo-3.1-lite';
    const canKeepCurrent = videoGenerationMode === 'motion' || (videoGenerationMode === 'reference' && !isLiteModel);
    if (mode === 'prompt' && videoGenerationMode !== 'frame' && next === 'frame' && canKeepCurrent) {
      const hasIncompatible = selectedAssets.some(a => !isCompatibleFrameAspectRatio(a));
      if (hasIncompatible) {
        // canKeepCurrent が reference / motion を保証する
        setKeepReferenceFromMode(videoGenerationMode === 'motion' ? 'motion' : 'reference');
        setShowKeepReferenceConfirm(true);
        return;
      }
    }
    setVideoGenerationMode(next);
  }, [mode, videoGenerationMode, selectedAssets, setVideoGenerationMode, videoSettings.videoModel]);

  const prevAssetIdsForAspectCheckRef = useRef<Set<string>>(new Set(selectedAssets.map(a => a.id)));
  useEffect(() => {
    const prevIds = prevAssetIdsForAspectCheckRef.current;
    const currentIds = new Set(selectedAssets.map(a => a.id));
    prevAssetIdsForAspectCheckRef.current = currentIds;

    if (mode !== 'prompt' || videoGenerationMode !== 'frame') return;
    const addedAssets = selectedAssets.filter(a => !prevIds.has(a.id));
    if (addedAssets.length === 0) return;
    if (addedAssets.some(a => !isCompatibleFrameAspectRatio(a))) {
      setShowSwitchToReferenceConfirm(true);
    }
  }, [selectedAssets, mode, videoGenerationMode]);

  // Seedance 系を選んだ瞬間に「顔を描かない」モードが OFF なら警告モーダルを出す
  // (Seedance 2.0 系は real human face を含む素材を直接アップロードできない制約があるため)
  const [showSeedanceFaceWarning, setShowSeedanceFaceWarning] = useState(false);
  // 逆に Veo を選んだ瞬間に「顔を描かない」モードが ON なら、オフを勧める案内を出す
  // (Veo は実在の顔を含む素材を扱えるため、目隠しバーを入れる本モードは不要・品質低下要因)
  const [showVeoFaceOffNotice, setShowVeoFaceOffNotice] = useState(false);
  // Seedance ↔ Veo 切替で俳優選択がクリアされる場合の確認モーダル。
  // 切替前にユーザーへ確認を取り、キャンセル時はモデルを元に戻す。
  const [actorClearConfirm, setActorClearConfirm] = useState<ActorClearConfirmState | null>(null);

  const previousVideoModelRef = useRef<string>(videoSettings.videoModel);
  useEffect(() => {
    const previous = previousVideoModelRef.current;
    const current = videoSettings.videoModel;
    if (previous === current) return;
    const wasSeedance = previous?.startsWith('seedance');
    const isSeedance = current?.startsWith('seedance');
    const wasVeo = previous?.startsWith('veo');
    const isVeo = current?.startsWith('veo');
    const isOmni = current?.startsWith('gemini-omni') || current?.startsWith('omni');

    // Seedance / Gemini Omni では旧「絵コンテをイラストで描く」フラグをデフォルト ON にする
    // (廃止済みトグル。絵コンテは線画へ全面移行し backend はフラグを無視するが、旧シナリオ
    // metadata との同期互換のため state は維持している)。
    if ((isSeedance || isOmni) && !illustrationStoryboard) {
      setIllustrationStoryboard(true);
      setAvoidFaces(false); // 「顔を描かない」とは排他
    }

    if (wasSeedance !== isSeedance) {
      // Seedance 側のテーブルの俳優選択がもう片方では使えないので、選択中の俳優が
      // クリアされる場合は事前に確認を取る（ref は更新せず、キャンセル時に
      // setVideoSettings で元に戻せば previous === current で再発火しない）。
      // ただし非Seedance→Seedanceへの切替では、キャラクター俳優 (ethnicity='character')
      // は Seedance でも引き続き選択可能 (計画書§4 P4/P5) なので、人間俳優が1体でも
      // 含まれる場合のみ確認・解除の対象にする (レビュー指摘 P2)。
      const fullCastForSwitch: CastingActor[] = selectedCasting
        ? [selectedCasting, ...additionalCastings]
        : additionalCastings;
      const castSplit = isSeedance ? splitCastForSeedanceSwitch(fullCastForSwitch) : null;
      const willClearCasting = !!castSplit?.needsConfirm;
      const willClearSeedanceActor = !isSeedance && !!selectedSeedancePresetActor;
      if (willClearCasting || willClearSeedanceActor) {
        setActorClearConfirm({
          fromModel: previous,
          toModel: current,
          side: willClearCasting ? 'castingToSeedance' : 'actorToVeo',
          keepCastActors: willClearCasting ? castSplit?.keepCast : undefined,
        });
        return;
      }
      // キャラクター俳優のみ選択中 (人間俳優を含まない) なら確認を出さず選択を維持する
      // (selectedCasting/additionalCastings は変更しない)。
      // 「絵コンテをイラストで描く」を勧める案内 (showSeedanceFaceWarning) は廃止。
      // 絵コンテは線画フォーマットへ全面移行し (2026-08-14)、トグルに依らず参照画像に
      // 実在の顔写真が含まれなくなったため、案内する対象が無い。
    }
    // Veo に切り替えた瞬間、avoidFaces が ON なら「オフを勧める」案内を表示
    // (kling→veo 等、Seedance 境界をまたがない切替も拾えるよう別判定にする)
    if (!wasVeo && isVeo && avoidFaces) {
      setShowVeoFaceOffNotice(true);
    }
    previousVideoModelRef.current = current;
  }, [videoSettings.videoModel, avoidFaces, illustrationStoryboard, videoGenerationMode, selectedCasting, additionalCastings, selectedSeedancePresetActor, castingGenderFilter, castingAgeFilter, setAvoidFaces, setIllustrationStoryboard]);

  // 全フレーム削除の確認 (WorkPanel が描画する)
  const [showClearAllFramesConfirm, setShowClearAllFramesConfirm] = useState(false);

  const onActorClearConfirm = useCallback(() => {
    if (!actorClearConfirm) return;
    if (actorClearConfirm.side === 'castingToSeedance') {
      // 人間俳優のみ解除し、キャラクター俳優 (計画書§4 P5) は維持する。keepCastActors は
      // フルキャスト順を保った「人間俳優を除いた配列」なので、先頭を次の主俳優へ昇格する。
      // 人間俳優のみ選択中だった場合は keepCastActors=[] のため従来どおり全解除になる。
      const keepCast = actorClearConfirm.keepCastActors ?? [];
      const [nextPrimary, ...nextAdditional] = keepCast;
      setSelectedCasting(nextPrimary ?? null);
      setAdditionalCastings(nextAdditional);
      // 「絵コンテをイラストで描く」案内 (showSeedanceFaceWarning) は廃止
      // (絵コンテは線画へ全面移行し案内する対象が無い)。
    } else {
      setSelectedSeedancePresetActor(null);
      setAdditionalSeedancePresetActors([]);
      // seedance→veo かつ avoidFaces オンなら、続けてオフを勧める案内も提示する
      if (actorClearConfirm.toModel.startsWith('veo') && avoidFaces) {
        setShowVeoFaceOffNotice(true);
      }
    }
    previousVideoModelRef.current = actorClearConfirm.toModel;
    setActorClearConfirm(null);
  }, [actorClearConfirm, videoGenerationMode, illustrationStoryboard, avoidFaces, setSelectedCasting, setAdditionalCastings, setSelectedSeedancePresetActor, setAdditionalSeedancePresetActors]);

  const onActorClearCancel = useCallback(() => {
    if (!actorClearConfirm) return;
    // モデルを元に戻す。ref は更新していないため再発火しない。
    const revertTo = actorClearConfirm.fromModel;
    setVideoSettings(prev => ({ ...prev, videoModel: revertTo as VideoSettings['videoModel'] }));
    setActorClearConfirm(null);
  }, [actorClearConfirm, setVideoSettings]);

  const onSeedanceFaceWarningConfirm = useCallback(() => {
    setIllustrationStoryboard(true);
    // 「顔を描かない」とは排他
    setAvoidFaces(false);
    setShowSeedanceFaceWarning(false);
  }, [setIllustrationStoryboard, setAvoidFaces]);

  const onSeedanceFaceWarningCancel = useCallback(() => setShowSeedanceFaceWarning(false), []);

  const onVeoFaceOffNoticeConfirm = useCallback(() => {
    setAvoidFaces(false);
    setShowVeoFaceOffNotice(false);
  }, [setAvoidFaces]);

  const onVeoFaceOffNoticeCancel = useCallback(() => setShowVeoFaceOffNotice(false), []);

  const onKeepReferenceConfirm = useCallback(() => {
    // 切替を押した時点のモードへ戻す (モーダル表示中にモードが変わっていても維持先は固定)
    setVideoGenerationMode(keepReferenceFromMode);
    setShowKeepReferenceConfirm(false);
  }, [keepReferenceFromMode, setVideoGenerationMode]);
  const keepReferenceModeLabel = keepReferenceFromMode === 'motion' ? 'モーショングラフィックス' : 'AIおまかせ';

  const onKeepReferenceCancel = useCallback(() => {
    setVideoGenerationMode('frame');
    setShowKeepReferenceConfirm(false);
  }, [setVideoGenerationMode]);

  const onSwitchToReferenceConfirm = useCallback(() => {
    setPromptVideoGenerationMode('reference');
    setShowSwitchToReferenceConfirm(false);
  }, [setPromptVideoGenerationMode]);

  const onSwitchToReferenceCancel = useCallback(() => setShowSwitchToReferenceConfirm(false), []);

  return {
    handleVideoGenerationModeChange,
    actorClearConfirm,
    onActorClearConfirm,
    onActorClearCancel,
    showSeedanceFaceWarning,
    onSeedanceFaceWarningConfirm,
    onSeedanceFaceWarningCancel,
    showVeoFaceOffNotice,
    onVeoFaceOffNoticeConfirm,
    onVeoFaceOffNoticeCancel,
    showKeepReferenceConfirm,
    keepReferenceModeLabel,
    onKeepReferenceConfirm,
    onKeepReferenceCancel,
    showSwitchToReferenceConfirm,
    onSwitchToReferenceConfirm,
    onSwitchToReferenceCancel,
    showClearAllFramesConfirm,
    setShowClearAllFramesConfirm,
  };
}
